import React, { useEffect, useRef, useState } from 'react';
import firebase from 'firebase/auth';
import { IProps } from '../types/iProps';
import { auth } from '../components/App/FirebaseSetup';
import axios from 'axios';
import { API_URL, FAKE_AUTH_TOKEN } from '../constants';
import { useUserContext } from './UserContext';

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !FAKE_AUTH_TOKEN) {
            window.location.href = '/admin/login';
        }
        return Promise.reject(error);
    }
);

interface IAuthContextProps {
    user: firebase.User | null;
    isLoading: boolean;
    signOut?: () => void;
}

export const AuthContext = React.createContext<IAuthContextProps>({
    user: null,
    isLoading: true,
});

export const AuthProvider: React.FC<IProps> = ({ children }) => {
    const [user, setUser] = useState<firebase.User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { updateUser } = useUserContext();

    const signOut = async () => {
        await auth.signOut();
    };

    useEffect(() => {
        if (FAKE_AUTH_TOKEN) {
            axios.defaults.baseURL = API_URL;
            axios.defaults.headers.common = {
                Authorization: `bearer ${FAKE_AUTH_TOKEN}`,
            };
            setUser({ uid: 'fake-user' } as firebase.User);
            setIsLoading(false);
            return;
        }

        const unsubscribe = auth.onAuthStateChanged(
            (firebaseUser) => {
                setUser(firebaseUser);
                setIsLoading(false);

                if (user) {
                    user.getIdToken().then(function (token) {
                        axios.defaults.baseURL = API_URL;
                        axios.defaults.headers.common = {
                            Authorization: `bearer ${token}`,
                        };

                        updateUser({
                            userPhone: user?.phoneNumber ?? '',
                            token,
                        });
                    });
                }
            },
            () => {
                setIsLoading(false);
            }
        );

        auth.onIdTokenChanged((user) => {
            user?.getIdToken().then((token) => {
                axios.defaults.baseURL = API_URL;
                axios.defaults.headers.common = {
                    Authorization: `bearer ${token}`,
                };
                console.log('ERROR!', user?.phoneNumber, token);
                updateUser({
                    userPhone: user?.phoneNumber ?? '',
                    token,
                });
            });
        });

        return unsubscribe;
    }, []);

    return <AuthContext.Provider value={{ user, isLoading, signOut }}>{children}</AuthContext.Provider>;
};
